
import "../css/styles.css";


import { useState, useEffect, useContext, createContext, useCallback, useRef } from "react";


import { DEBUG, DEBUG_VOICE_SPEED, DEFAULT_NOTIFICATION_LIFESPAN, FAKE_STUDENT_NAME, FAKE_STUDENT_VOICES, IDLE_INSTRUCTOR_VIDEO, INSTRUCTOR_NAME, INSTRUCTOR_VOICES, MAX_WAIT_BEFORE_TRY_SPEAK, MIN_SILENCE_BEFORE_NEXT_SPEAK, QUESTIONS_PAUSED_NOTIFICATION, SERVER_ORIGIN, USE_BROWSER_SPEECH_SYNTHESIS_API, VERBOSE, VOICE_SPEED, ENABLE_AUDIO_OUTPUT, USE_TEXT_LENGTH_BASED_DELAY, AUDIO_OUTPUT_FIXED_DELAY, AUDIO_OUTPUT_WORDS_PER_MINUTE } from "../common/constants";


import { useSession, useSessionUpdate } from "./SessionContext";
import { useNotificationUpdate } from "./NotificationContext";

import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

import ReportsService from "../services/reports";

import TimeService from "../services/time";
import { fetchOpenAITextToSpeech } from "../services/fetch";

import { SpeakerType } from "../types/enums";


export {
  SpeechSynthesisContextProvider,
  useSpeechSynthesis, useSpeechSynthesisUpdate
};


const SpeechSynthesisContext = createContext<any>( null ); // TODO: Properly fill out

const SpeechSynthesisUpdateContext = createContext<any>( null ); // TODO: Properly fill out


function useSpeechSynthesis() {
  return useContext( SpeechSynthesisContext );
}

function useSpeechSynthesisUpdate() {
  return useContext( SpeechSynthesisUpdateContext );
}


// ========================== AUDIO HELPER FUNCTIONS ==========================

/**
 * Calculate the delay duration for audio playback simulation when ENABLE_AUDIO_OUTPUT is false
 * @param text - The text that would be spoken
 * @returns Delay in milliseconds
 */
function calculateAudioDelay(text: string): number {
  if (!USE_TEXT_LENGTH_BASED_DELAY) {
    return AUDIO_OUTPUT_FIXED_DELAY;
  }

  // Calculate based on word count and words per minute
  const words = text.trim().split(/\s+/).filter(word => word.length > 0);
  const wordCount = words.length;

  // Convert words per minute to milliseconds per word
  const millisecondsPerWord = (60 * 1000) / AUDIO_OUTPUT_WORDS_PER_MINUTE;
  const duration = Math.ceil(wordCount * millisecondsPerWord);

  if (DEBUG && VERBOSE) {
    console.log(`[Audio] Calculated delay for ${wordCount} words: ${duration}ms (${AUDIO_OUTPUT_WORDS_PER_MINUTE} WPM)`);
  }

  return duration;
}

/**
 * Create a mock audio object that simulates audio playback with events
 * Used when ENABLE_AUDIO_OUTPUT is false to maintain the same event flow
 * @param text - The text that would be spoken (used for delay calculation)
 * @param audioGeneration - The generation number for invalidation tracking
 * @param unmuteAfter - Whether to unmute students after playback
 * @param startAudioEvent - The event name for audio start (e.g., 'play' or 'start')
 * @param endAudioEvent - The event name for audio end (e.g., 'ended' or 'end')
 * @returns Mock audio object with event handling
 */
function createMockAudio(
  text: string,
  audioGeneration: number,
  unmuteAfter: boolean,
  startAudioEvent: string,
  endAudioEvent: string,
  muteStudents: () => void,
  unmuteStudents: () => void,
  questionsAllowed: boolean,
  getSlideGeneration: () => number,
  setSomeoneSpeaking: (speaking: boolean) => void,
  resetAudioTranscript: () => void
): any {

  const delay = calculateAudioDelay(text);

  const mockAudio: any = {
    _generation: audioGeneration,
    _eventListeners: {} as any,
    _startTimeoutId: null as any,
    _endTimeoutId: null as any,
    playbackRate: 1.0,

    addEventListener(event: string, handler: Function) {
      if (!this._eventListeners[event]) {
        this._eventListeners[event] = [];
      }
      this._eventListeners[event].push(handler);
    },

    removeEventListener(event: string, handler: Function) {
      if (this._eventListeners[event]) {
        this._eventListeners[event] = this._eventListeners[event].filter((h: Function) => h !== handler);
      }
    },

    dispatchEvent(event: string) {
      if (this._eventListeners[event]) {
        this._eventListeners[event].forEach((handler: Function) => {
          try {
            handler();
          } catch (error) {
            console.error(`[Audio Mock] Error in ${event} handler:`, error);
          }
        });
      }
    },

    play() {
      // Don't play if generation has changed
      if (this._generation !== getSlideGeneration()) {
        if (DEBUG && VERBOSE) {
          console.log(`[Audio Mock] Skipping play: generation mismatch (mock: ${this._generation}, current: ${getSlideGeneration()})`);
        }
        return Promise.resolve();
      }

      if (DEBUG && VERBOSE) {
        console.log(`[Audio Mock] Starting simulated playback: ${delay}ms delay`);
      }

      // Simulate immediate 'play'/'start' event
      this._startTimeoutId = setTimeout(() => {
        this.dispatchEvent(startAudioEvent);
      }, 10);

      // Simulate 'ended'/'end' event after delay
      this._endTimeoutId = setTimeout(() => {
        // Check generation before firing end event
        if (this._generation === getSlideGeneration()) {
          this.dispatchEvent(endAudioEvent);
        } else {
          // Even on generation mismatch, always reset someoneSpeaking to prevent stuck state
          setSomeoneSpeaking(false);
          if (DEBUG && VERBOSE) {
            console.log(`[Audio Mock] Generation mismatch but still resetting someoneSpeaking (mock: ${this._generation}, current: ${getSlideGeneration()})`);
          }
        }
      }, delay);

      return Promise.resolve();
    },

    pause() {
      if (DEBUG && VERBOSE) {
        console.log('[Audio Mock] Pausing simulated playback');
      }
      // Clear timeouts to stop event firing
      if (this._startTimeoutId) clearTimeout(this._startTimeoutId);
      if (this._endTimeoutId) clearTimeout(this._endTimeoutId);
    },

    // Properties that might be accessed
    currentTime: 0,
    duration: delay / 1000,
    paused: false,
    ended: false,
    onerror: null,
    onended: null,
    onplay: null
  };

  return mockAudio;
}

// =============================================================================




function SpeechSynthesisContextProvider( {children}: ComponentProps ) {


  // TODO: FINISH implementing Avatar Video (see ChatContainer for HTML and where avatarVideoFileName is used)
  const [ avatarVideoFileName, setAvatarVideoFileName ] = useState<string>();

  const speaking = useRef( false );

  const { questionsAllowed, someoneSpeaking } = useSession();
  const { setSomeoneSpeaking, setSlideReadyToMoveOn, muteStudents, unmuteStudents, getSlideGeneration, incrementSlideGeneration } = useSessionUpdate();

  const { postNotification } = useNotificationUpdate();


  const speechSynthesis = useRef<any>();
  const activeAudioObjects = useRef<Set<any>>(new Set()); // Track all active audio objects


  const {
    listening,

    transcript,
    resetTranscript: resetAudioTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();



  const startAudioEvent = USE_BROWSER_SPEECH_SYNTHESIS_API ? "start" : "play";
  const endAudioEvent = USE_BROWSER_SPEECH_SYNTHESIS_API ? "end" : "ended";





  useEffect( () => {
    window.speechSynthesis.resume();
    cancelSpeechSynthesis();
  }, []);



  // ========================== AUDIO / VOICE HANDLING ==========================
  window.speechSynthesis.onvoiceschanged = () => getAvailableDesiredVoices;

  const getAvailableDesiredVoices = useCallback( (otherVoices = [...FAKE_STUDENT_VOICES, ...INSTRUCTOR_VOICES]) => {
    const voices = window.speechSynthesis.getVoices();

    const fineButNotGreatVoices = ["Zira", "Mark"]; // EDGE (at least)
    //"Google UK English Female", "Google UK English Male", "Google US English"]; // CHROME // Specifically listed here since shown available in Chrome as well. Listed separately since they're not great voices, but better than default
    let desiredVoiceNames = ["Yan", "Jenny", "Aria", "Roger", "Steffan", ...fineButNotGreatVoices, ...otherVoices];

    let availableDesiredVoices = voices.filter(voice =>
      desiredVoiceNames.some(desiredVoice => voice.name.includes(desiredVoice))
    );

    if (DEBUG && VERBOSE) { console.groupCollapsed("Available Desired Voices"); console.log(availableDesiredVoices); console.groupEnd(); }

    if (availableDesiredVoices == null) console.warn("No desired Voice available");

    return availableDesiredVoices;
  }, []);


  const doBrowserSpeechSynthesis = useCallback( ( text: string, speaker: string, idealVoiceNames: string[], unmuteAfter: boolean ) => {

    //window.speechSynthesis.cancel();

    // TODO: Get more robust for specific Browsers
    // Voices that sound good (Edge Browser):
    // -- Female: Yan, Jenny, Aria
    // -- Male: Roger, Steffan

    if( DEBUG && VERBOSE ) console.log("IDEAL VOICE(S): ", idealVoiceNames);

    const availableDesiredVoices = getAvailableDesiredVoices();

    let chosenVoice: any = availableDesiredVoices.find( voice =>
      idealVoiceNames.some(idealVoice => voice.name.includes(idealVoice))
    );
    if( chosenVoice == null ) { // If we didn't find the Ideal Voice in Available Desired Voices...
      console.warn(`WARNING: ${idealVoiceNames} (ideal) Voice(s) not available`);
      chosenVoice = availableDesiredVoices ? availableDesiredVoices[0] : null
    }

    const utterance = new SpeechSynthesisUtterance(text);

    // Capture the current generation for this utterance
    const utteranceGeneration = getSlideGeneration();
    (utterance as any)._generation = utteranceGeneration;

    if( chosenVoice ) utterance.voice = chosenVoice;

    if( DEBUG ) utterance.rate = DEBUG_VOICE_SPEED;
    else utterance.rate = VOICE_SPEED;

    //setSomeoneSpeaking( true );
    setSlideReadyToMoveOn( false );
    muteStudents();

    const originalEndHandler = () => {
      // Check if this utterance is from a stale generation
      if ((utterance as any)._generation !== getSlideGeneration()) {
        return;
      }
      //setSomeoneSpeaking( false );
      if( unmuteAfter && questionsAllowed ) unmuteStudents();
    };

    utterance.addEventListener( endAudioEvent, originalEndHandler );

    return utterance; // Allows Caller to add additional Event Listeners (NOTE: Can add multiple "end" Events, or whatever, and will each be executed)
  }, []);
  const doOpenAITextToSpeech = useCallback( async ( text: string, speaker: string, unmuteAfter: boolean ) => {

    const textToConvert = text;

    let voiceName = "nova"; // TODO: Sync with server's names... use an API call?
    switch( speaker ) {
      case INSTRUCTOR_NAME: {
        voiceName = "nova";
        break;
      }

      case FAKE_STUDENT_NAME: {
        voiceName = "fable";
        break;
      }

      default: {
        console.error( `Unrecognized Speaker: ${speaker}` );
        break;
      }
    }

    // Capture generation before async fetch
    const audioGeneration = getSlideGeneration();

    try {
      const audioBlob = await fetchOpenAITextToSpeech( textToConvert, voiceName );

      // Abort if slide changed during fetch
      if (audioGeneration !== getSlideGeneration()) {
        return null;
      }

      if( !audioBlob ) {
        console.error("ERROR: Fetching OpenAI Text To Speech Failed");
        return null;
      }

      const audioUrl = URL.createObjectURL( audioBlob );
      let audio: any = new Audio( audioUrl );

      // Store the generation that was captured BEFORE the fetch
      audio._generation = audioGeneration;

      // Track this audio object
      activeAudioObjects.current.add(audio);


      audio.addEventListener( endAudioEvent, () => {
        URL.revokeObjectURL( audioUrl );
      });

      const handleError = (e: any) => console.error( "ERROR DURING PLAYBACK:", e );
      audio.onerror = handleError;
      //audio.play();



      if( !audio ) {
        // TODO: Error Handle
        console.error("ERROR: Creating Audio Object Failed");
      }


      // TODO: See if better way, hacking this together to prevent repeated audio when Pause/UnPause after playback finishes (i.e. it caused audio to repeat again)
      // ... Doing something similar in postMessageAndSpeak in MessageContext
      const cleanupAudio = () => {
        //audio.removeEventListener( "error", handleError );
        //audio.onerror = null;

        // Remove from active audio tracking
        activeAudioObjects.current.delete(audio);

        URL.revokeObjectURL( audioUrl );

        /*
        audio.removeEventListener( endAudioEvent, cleanupAudio );
        audio.src = ""; // Clear the source
        audio.load(); // Stop the audio from being used
        audio = null; // Dereference the audio object
        */
      };

      // Safety timeout to prevent someoneSpeaking from getting stuck
      const SPEAKING_SAFETY_TIMEOUT = 60000; // 60 seconds max for any audio
      let speakingTimeoutId: any = null;

      const startAudio = () => {
        // Don't play if slide changed
        if (audio._generation !== getSlideGeneration()) {
          audio.pause();
          audio.currentTime = 0;
          cleanupAudio();
          return;
        }
        setSomeoneSpeaking( true );

        // Start safety timeout to reset someoneSpeaking if audio events fail
        speakingTimeoutId = setTimeout(() => {
          if (DEBUG) {
            console.warn('[Audio] Safety timeout: resetting someoneSpeaking after 60s');
          }
          setSomeoneSpeaking( false );
        }, SPEAKING_SAFETY_TIMEOUT);
      }

      const endAudio = () => {
        // Clear safety timeout since audio ended properly
        if (speakingTimeoutId) {
          clearTimeout(speakingTimeoutId);
          speakingTimeoutId = null;
        }

        // Always reset someoneSpeaking, even on generation mismatch, to prevent stuck state
        setSomeoneSpeaking( false );

        // Don't continue with other cleanup if slide changed
        if (audio._generation !== getSlideGeneration()) {
          cleanupAudio();
          return;
        }

        resetAudioTranscript();
        if( unmuteAfter && questionsAllowed ) unmuteStudents();

        audio.removeEventListener( endAudioEvent, endAudio );
        cleanupAudio();
      }

      audio.addEventListener( startAudioEvent, startAudio );
      audio.addEventListener( endAudioEvent, endAudio );

      if( DEBUG && VERBOSE ) console.log("Speak Function Executing -- Passed Checks");

      //setSlideReadyToMoveOn( false ); // If someone spoke recently, then we're not ready to move on
      muteStudents();


      // ========= TODO TESTING TEMP =========
      audio.playbackRate = DEBUG ? DEBUG_VOICE_SPEED : VOICE_SPEED; // TODO: Testing DEBUG
      //audio.play();

      speechSynthesis.current = audio;
      // =====================================


      return audio;

    } catch( error ) {
      console.error("Error Playing Audio:", error);
      return null;
    }
  }, []);
  const doThirdPartySpeechSynthesis = useCallback( async ( text: string, speaker: string, unmuteAfter: boolean ) => {
    return doOpenAITextToSpeech( text, speaker, unmuteAfter );
  },[])
 
  const speak = useCallback( async ( text: string, { idealVoiceNames = INSTRUCTOR_VOICES, speaker = INSTRUCTOR_NAME, unmuteAfter = true, avatarVideo = IDLE_INSTRUCTOR_VIDEO } = {} ) => {

    resetAudioTranscript(); // Safety measure to help ensure studen't mic not pick up bot's audio (which may happen )

    //playIdleVideo(avatarVideo);

    // If audio output is disabled, return a mock audio object that simulates playback
    if (!ENABLE_AUDIO_OUTPUT) {
      const audioGeneration = getSlideGeneration();
      const mockAudio = createMockAudio(
        text,
        audioGeneration,
        unmuteAfter,
        startAudioEvent,
        endAudioEvent,
        muteStudents,
        unmuteStudents,
        questionsAllowed,
        getSlideGeneration,
        setSomeoneSpeaking,
        resetAudioTranscript
      );

      if (DEBUG && VERBOSE) {
        console.log('[Audio] Using mock audio (ENABLE_AUDIO_OUTPUT = false)');
      }

      // Still track this mock audio object
      activeAudioObjects.current.add(mockAudio);

      // Set as current speechSynthesis for pause/resume to work
      speechSynthesis.current = mockAudio;

      return mockAudio;
    }

    if( USE_BROWSER_SPEECH_SYNTHESIS_API ) {
      //const utterance = doBrowserSpeechSynthesis(text, messageElementId, idealVoiceNames, unmuteAfter, avatarVideo);
      const utterance = doBrowserSpeechSynthesis(text, speaker, idealVoiceNames, unmuteAfter);
      return utterance;
    }
    else {
      const audio = await doThirdPartySpeechSynthesis( text, speaker, unmuteAfter );
      return audio;
    }

  }, []);




  //              ===== Speech Synthesis APIs =====
  // Separate Functions (even for simple Operations) in case implementation varies between Browsers or APIs we may later use (thus needing logic to properly handle -- this way caller not need change)

  // TODO: Decide if Pause/Resume should change "someoneSpeaking" (may have unintended side-effects like enablng/disabling "someoneSpeaking" when we don't intend to)
  const pauseSpeechSynthesis = useCallback( (  ) => { // All Utterances in Queue not Spoken till UnPaused

    if (USE_BROWSER_SPEECH_SYNTHESIS_API) {
      window.speechSynthesis.pause();
    }
    else {
      if( speechSynthesis?.current ) speechSynthesis.current.pause();
    }

    //sessionDetails.someoneSpeaking = false; // TODO - EXPERIMENTAL
    //if( questionsAllowed ) unmuteStudents();
  }, []);

  const resumeSpeechSynthesis = useCallback( (  ) => { // Continues playing Utterances in Queue
    // Only mute students and resume audio if we actually have audio to resume
    // This fixes the bug where students get stuck muted even when nothing is playing
    const hasActiveAudio = speechSynthesis?.current &&
      (USE_BROWSER_SPEECH_SYNTHESIS_API || activeAudioObjects.current.size > 0);

    if( !hasActiveAudio ) {
      // No audio to resume - don't mute students unnecessarily
      if( DEBUG && VERBOSE ) {
        console.log('[Audio] resumeSpeechSynthesis called but no active audio to resume');
      }
      return;
    }

    muteStudents();

    if( USE_BROWSER_SPEECH_SYNTHESIS_API ) {
      window.speechSynthesis.resume();
    }
    else {
      if( speechSynthesis?.current ) speechSynthesis.current.play();
    }
  }, []);

  const cancelSpeechSynthesis = useCallback( () => {

    if( USE_BROWSER_SPEECH_SYNTHESIS_API ) {
      window.speechSynthesis.cancel();
      incrementSlideGeneration();
    }
    else {
      // Stop and clean up ALL active audio objects
      activeAudioObjects.current.forEach((audio) => {
        try {
          audio.pause();
          audio.currentTime = 0;
          // Remove event listeners to prevent them from firing
          audio.onended = null;
          audio.onplay = null;
          audio.onerror = null;
        } catch (error) {
          console.error("Error stopping audio:", error);
        }
      });

      // Clear the set of active audio objects
      activeAudioObjects.current.clear();

      // Also handle the current speechSynthesis reference
      if( speechSynthesis?.current ) {
        speechSynthesis.current.pause();
        speechSynthesis.current.currentTime = 0;
        speechSynthesis.current = null;
      }

      // Increment generation to invalidate any in-flight audio
      incrementSlideGeneration();
    }

    setSomeoneSpeaking( false );
  },[incrementSlideGeneration]); 

  // =============================================================================






  return (
    <SpeechSynthesisContext.Provider value={ {speak, avatarVideoFileName} } >
      <SpeechSynthesisUpdateContext.Provider value={ {pauseSpeechSynthesis, resumeSpeechSynthesis, cancelSpeechSynthesis} } >
        {children}
      </SpeechSynthesisUpdateContext.Provider>
    </SpeechSynthesisContext.Provider>
  );


}










/* */